
import { useState, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import {
  inventoryCategoryOptions,
  paymentTypeOptions,
  paymentCadenceOptions,
} from "./inventoryTypes";

const formSchema = z.object({
  requested_by: z.string().min(1, { message: "Requester is required" }),
  description: z.string().min(1, { message: "Description is required" }),
  justification: z.string().optional(),
  category: z.string().min(1, { message: "Category is required" }),
  estimated_cost: z.string().min(1, { message: "Estimated cost is required" }),
  currency: z.string().default("USD"),
  payment_type: z.string().min(1, { message: "Payment type is required" }),
  payment_cadence: z.string().optional(),
  payment_amount: z.string().optional(),
});

interface NewPurchaseRequestDialogProps {
  open: boolean;
  onClose: (created: boolean) => void;
}

export function NewPurchaseRequestDialog({ open, onClose }: NewPurchaseRequestDialogProps) {
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [employeeNotFoundError, setEmployeeNotFoundError] = useState(false);
  const [employees, setEmployees] = useState<Array<{ id: number; name: string; email: string }>>([]);
  const { toast } = useToast();
  
  const fetchEmployees = async () => {
    try {
      const { data, error } = await supabase
        .from("employees")
        .select("id, name, email");
        
      if (error) throw error;
      
      setEmployees(data || []);
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };
  
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        setLoading(true);
        setEmployeeNotFoundError(false);
        
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setEmployeeNotFoundError(true);
          return;
        }
        
        // Find the employee record associated with the user
        const { data: employeeData } = await supabase
          .from("employees")
          .select("id, email")
          .eq("email", user.email);
          
        if (employeeData && employeeData.length > 0) {
          setCurrentUserId(employeeData[0].id);
          
          // Set the requested_by field value
          form.setValue("requested_by", employeeData[0].id.toString());
        } else {
          setEmployeeNotFoundError(true);
          console.error("No employee record found for the current user");
        }
      } catch (error) {
        setEmployeeNotFoundError(true);
        console.error("Failed to fetch current user:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCurrentUser();
    fetchEmployees();
  }, []);
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      requested_by: "",
      description: "",
      justification: "",
      category: "Hardware",
      estimated_cost: "",
      currency: "USD",
      payment_type: "One Time",
      payment_cadence: "Monthly", // Set default to Monthly
      payment_amount: "",
    },
  });
  
  const watchPaymentType = form.watch("payment_type");
  
  // Update payment cadence when payment type changes
  useEffect(() => {
    if (watchPaymentType === "Recursive") {
      form.setValue("payment_cadence", "Monthly");
    }
  }, [watchPaymentType, form]);
  
  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    try {
      const purchaseRequest = {
        requested_by: parseInt(data.requested_by),
        description: data.description,
        justification: data.justification || null,
        estimated_cost: parseFloat(data.estimated_cost),
        currency: data.currency,
        category: data.category,
        payment_type: data.payment_type,
        status: "Pending",
      } as any;
      
      // Add recursive payment details if applicable
      if (data.payment_type === "Recursive") {
        purchaseRequest.payment_cadence = data.payment_cadence;
        
        if (data.payment_amount) {
          purchaseRequest.payment_amount = parseFloat(data.payment_amount);
        }
      }
      
      const { error } = await supabase
        .from("purchase_requests")
        .insert(purchaseRequest);
        
      if (error) throw error;
      
      toast({ title: "Success", description: "Purchase request created successfully" });
      onClose(true);
    } catch (error) {
      console.error("Error creating purchase request:", error);
      toast({ 
        title: "Error", 
        description: "Failed to create purchase request", 
        variant: "destructive" 
      });
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose(false)}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>New Purchase Request</DialogTitle>
          <DialogDescription>
            Fill out this form to request purchase approval
          </DialogDescription>
        </DialogHeader>
        
        {employeeNotFoundError && (
          <Alert variant="destructive" className="mb-4">
            <Info className="h-4 w-4 mr-2" />
            <AlertDescription>
              No employee record found for your account. Please contact your administrator to create an employee profile for you.
            </AlertDescription>
          </Alert>
        )}
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="requested_by"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Requested By</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    disabled={loading}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select requester" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {employees.length === 0 ? (
                        <SelectItem value="loading">Loading employees...</SelectItem>
                      ) : (
                        employees.map((employee) => (
                          <SelectItem key={employee.id} value={employee.id.toString()}>
                            {employee.name} ({employee.email})
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., New Laptop for Developer" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {inventoryCategoryOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="estimated_cost"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Estimated Cost (USD)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="payment_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Payment Type</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select payment type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {paymentTypeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            {watchPaymentType === "Recursive" && (
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="payment_cadence"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Payment Cadence</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value || "Monthly"}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select payment cadence" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {paymentCadenceOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="payment_amount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Payment Amount (USD)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}
            
            <FormField
              control={form.control}
              name="justification"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Justification</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Why is this purchase necessary?"
                      className="min-h-[100px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onClose(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading || employeeNotFoundError || !form.getValues().requested_by}
              >
                Submit Request
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
